import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ImportSummary, ImportResult } from '../../../../types/domain-management.types';

@Component({
    selector: 'mon-import-wizard-results',
    templateUrl: './results.component.html',
    styleUrls: ['./results.component.scss'],
    standalone: false,
})
export class ImportWizardResultsComponent {
    @Input() importSummary: ImportSummary | null = null;
    @Output() downloadErrorReport = new EventEmitter<void>();

    // Pagination for results
    currentTab: 'success' | 'failed' = 'success';
    currentPage = 1;
    pageSize = 10;

    /**
     * Get successful results
     */
    getSuccessfulResults (): ImportResult[] {
        return this.importSummary?.results?.filter((result: ImportResult) => result.success) || [];
    }

    /**
     * Get failed results
     */
    getFailedResults (): ImportResult[] {
        return this.importSummary?.results?.filter((result: ImportResult) => !result.success) || [];
    }

    /**
     * Get paginated results based on current tab
     */
    getPaginatedResults (): ImportResult[] {
        const results = this.currentTab === 'success' ? this.getSuccessfulResults() : this.getFailedResults();
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return results.slice(startIndex, endIndex);
    }

    /**
     * Get total pages for current tab
     */
    getTotalPages (): number {
        const results = this.currentTab === 'success' ? this.getSuccessfulResults() : this.getFailedResults();
        return Math.ceil(results.length / this.pageSize);
    }

    /**
     * Switch to tab
     */
    switchTab (tab: 'success' | 'failed'): void {
        this.currentTab = tab;
        this.currentPage = 1;
    }

    /**
     * Navigate to page
     */
    goToPage (page: number): void {
        const totalPages = this.getTotalPages();
        if (page >= 1 && page <= totalPages) {
            this.currentPage = page;
        }
    }

    /**
     * Get page numbers for pagination
     */
    getPageNumbers (): number[] {
        const pages: number[] = [];
        const totalPages = this.getTotalPages();
        const maxVisible = 5;
        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);

        // Adjust start if we're near the end
        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * Get success rate percentage
     */
    getSuccessRate (): number {
        if (!this.importSummary?.totalDomains) {
            return 0;
        }
        return (this.importSummary.successCount / this.importSummary.totalDomains) * 100;
    }

    /**
     * Get import duration
     */
    getImportDuration (): string {
        if (!this.importSummary?.startedAt || !this.importSummary?.completedAt) {
            return 'Unknown';
        }

        const duration = new Date(this.importSummary.completedAt).getTime() - new Date(this.importSummary.startedAt).getTime();
        const seconds = Math.floor(duration / 1000);

        if (seconds < 60) {
            return `${seconds}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    }

    /**
     * Get domains per second rate
     */
    getImportRate (): string {
        if (!this.importSummary?.startedAt || !this.importSummary?.completedAt || !this.importSummary?.totalDomains) {
            return 'N/A';
        }

        const duration = new Date(this.importSummary.completedAt).getTime() - new Date(this.importSummary.startedAt).getTime();
        const seconds = duration / 1000;
        const rate = this.importSummary.totalDomains / seconds;

        if (rate < 1) {
            return `${(rate * 60).toFixed(1)}/min`;
        } else {
            return `${rate.toFixed(1)}/sec`;
        }
    }

    /**
     * Check if import was successful overall
     */
    isImportSuccessful (): boolean {
        return this.importSummary?.failureCount === 0;
    }

    /**
     * Get status color class
     */
    getStatusColorClass (): string {
        if (this.isImportSuccessful()) {
            return 'text-success';
        } else if (this.importSummary?.successCount && this.importSummary.successCount > 0) {
            return 'text-warning';
        } else {
            return 'text-danger';
        }
    }

    /**
     * Get status icon
     */
    getStatusIcon (): string {
        if (this.isImportSuccessful()) {
            return 'fas fa-check-circle';
        } else if (this.importSummary?.successCount && this.importSummary.successCount > 0) {
            return 'fas fa-exclamation-triangle';
        } else {
            return 'fas fa-times-circle';
        }
    }

    /**
     * Get status message
     */
    getStatusMessage (): string {
        if (this.isImportSuccessful()) {
            return `All ${this.importSummary?.totalDomains} domains imported successfully`;
        } else if (this.importSummary?.successCount && this.importSummary.successCount > 0) {
            return `${this.importSummary.successCount} of ${this.importSummary.totalDomains} domains imported successfully`;
        } else {
            return `Import failed - no domains were created`;
        }
    }

    /**
     * Download error report
     */
    onDownloadErrorReport (): void {
        this.downloadErrorReport.emit();
    }

    /**
     * Get error preview (first few characters)
     */
    getErrorPreview (error: string): string {
        if (error.length <= 100) {
            return error;
        }
        return error.substring(0, 100) + '...';
    }
}
